import { Outlet, useLocation } from "react-router";
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from "react";
import { MatrixCanvas } from "./MatrixCanvas";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

export function Root() {
  const { pathname, hash, search } = useLocation();


  const [CursorComponent, setCursorComponent] = useState<ComponentType | null>(null);
  const lenisControllerRef = useRef<{
    raf: (time: number) => void;
    destroy: () => void;
    scrollTo: (target: number, options?: { immediate?: boolean; force?: boolean }) => void;
  } | null>(null);
  const lenisRafIdRef = useRef<number | null>(null);


  // Handle hash-based scrolling when navigating from another page
  useLayoutEffect(() => {
    if (hash) {
      const anchorElement = document.querySelector(hash);
      if (anchorElement) {
        anchorElement.scrollIntoView({ behavior: "instant" as ScrollBehavior });
      }
      return;
    }
    // Lenis garde sa propre position : sans ce reset, il ramène l'ancienne position de défilement.
    lenisControllerRef.current?.scrollTo(0, { immediate: true, force: true });
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);

  useEffect(() => {
    // Le navigateur ne doit pas restaurer l'ancienne position par-dessus notre retour en haut.
    window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    const canonicalHref = window.location.href;
    let canonicalLink = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalHref);
  }, [pathname, hash, search]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouchLikeDevice = window.matchMedia("(hover: none), (pointer: coarse)").matches;

    document.documentElement.style.scrollBehavior = "auto";

    if (prefersReducedMotion || isTouchLikeDevice) {
      if (import.meta.env.DEV) {
        console.info(
          `[Lenis] smooth scroll désactivé — prefers-reduced-motion: ${prefersReducedMotion}, appareil tactile: ${isTouchLikeDevice}`,
        );
      }
      return;
    }

    if (import.meta.env.DEV) {
      console.info("[Lenis] smooth scroll actif");
    }

    let isDisposed = false;

    const initializeLenis = async () => {
      const module = await import("lenis");
      if (isDisposed) return;

      const Lenis = module.default;
      const lenisInstance = new Lenis({
        lerp: 0.08,
        smoothWheel: true,
        syncTouch: false,
        wheelMultiplier: 1,
        touchMultiplier: 1,
        autoRaf: false,
        overscroll: false,
      });

      lenisControllerRef.current = lenisInstance;

      const renderLenisFrame = (time: number) => {
        lenisControllerRef.current?.raf(time);
        lenisRafIdRef.current = requestAnimationFrame(renderLenisFrame);
      };

      lenisRafIdRef.current = requestAnimationFrame(renderLenisFrame);
    };

    void initializeLenis();

    return () => {
      isDisposed = true;
      if (lenisRafIdRef.current !== null) {
        cancelAnimationFrame(lenisRafIdRef.current);
        lenisRafIdRef.current = null;
      }
      lenisControllerRef.current?.destroy();
      lenisControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const isDesktopPointerEnvironment = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!isDesktopPointerEnvironment) {
      setCursorComponent(null);
      return;
    }

    let isUnmounted = false;

    const loadCustomCursor = async () => {
      const module = await import("./CustomCursor");
      if (!isUnmounted) {
        setCursorComponent(() => module.CustomCursor);
      }
    };

    void loadCustomCursor();

    return () => {
      isUnmounted = true;
    };
  }, [pathname]);

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "var(--background)",
        color: "var(--foreground)",
        overflowX: "hidden",
      }}
    >
      {/* Matrix rain background */}
      <MatrixCanvas />

      {/* Global custom cursor (desktop only, lazy loaded) */}
      {CursorComponent ? <CursorComponent /> : null}

      {/* Deep black base gradient */}
      <div
        className="fixed inset-0 pointer-events-none transition-colors duration-300"
        style={{
          background: "linear-gradient(180deg, #000 0%, #010801 40%, #000 100%)",
          zIndex: -1,
        }}
      />

      {/* Navigation */}
      <Navbar />

      {/* Page content */}
      <main style={{ position: "relative", zIndex: 1 }}>
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />

      {/* Global styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 6px #00ff41; }
          50% { opacity: 0.5; box-shadow: 0 0 12px #00ff41; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(250%) skewX(-12deg); }
        }
        /* Boucles infinies de la CTA en CSS plutôt qu'en motion : plus de JS à chaque frame. */
        @keyframes cta-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        @keyframes cta-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(1.08); }
        }
        @keyframes process-grid-scroll {
          from { transform: translateY(0); }
          to { transform: translateY(70px); }
        }
        .cta-float { animation: cta-float 5s ease-in-out infinite; }
        .process-grid-scroll { animation: process-grid-scroll 1.8s linear infinite; }
        .cta-dot { animation: cta-dot 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .cta-float, .cta-dot, .process-grid-scroll { animation: none; }
        }

        html { scroll-behavior: auto; }

        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #000; }
        ::-webkit-scrollbar-thumb { background: rgba(0, 255, 65, 0.2); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(0, 255, 65, 0.4); }

        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
}